import { PropsWithChildren, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AuthenticationResult,
  Configuration as MSALConfig,
  EventType,
  PublicClientApplication
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";

import { selectAuthenticationToken } from "Store/Selectors/Authentication";
import { uiLoginUsernameSet, uiSetAuthorizationToken } from "Store/Actions";
import { aquireToken } from "API/Authorization";
import { Configuration } from "Utils/Configuration";
import { IAuthenticationProviderProps } from "./IAuthenticationProviderProps";
import { WithConfiguration } from "../Configuration";

export const AuthenticationProvider = WithConfiguration(
  (props: PropsWithChildren<IAuthenticationProviderProps>): JSX.Element => {
    const { config, isConfigReady, children } = props;
    const dispatch = useDispatch();

    const instance = useMemo(() => {
      if (!isConfigReady) {
        return undefined;
      }

      return new PublicClientApplication(FromConfig(config));
    }, [isConfigReady, config]);

    const currentAuthToken = useSelector(selectAuthenticationToken);

    useEffect(() => {
      if (instance == null) {
        return () => undefined;
      }

      const callbackId = instance.addEventCallback(async ({ eventType, payload }) => {
        if (!isValidEventType(eventType)) {
          return;
        }

        const result = payload as AuthenticationResult;
        if (result == null) {
          await aquireToken(instance, config);
          return;
        }

        instance.setActiveAccount(result.account);
        if (currentAuthToken !== result.accessToken) {
          dispatch(uiLoginUsernameSet(result.account.username));
          dispatch(uiSetAuthorizationToken(result.accessToken));
        }
      });

      return () => {
        if (callbackId == null) {
          return;
        }

        instance.removeEventCallback(callbackId);
      };
    }, [config, instance, dispatch, currentAuthToken]);

    if (!isConfigReady) {
      return null;
    }

    return <MsalProvider instance={instance}>{children}</MsalProvider>;
  }
);

function FromConfig(config: Configuration): MSALConfig {
  const authConfig = config.getAuth();

  return {
    auth: {
      clientId: authConfig.clientId,
      authority: `${authConfig.instance.slice(0, -1)}/${authConfig.tenantId}`,
      redirectUri: authConfig.redirectUri
    }
  };
}

function isValidEventType(eventType: EventType): boolean {
  return [EventType.LOGIN_SUCCESS, EventType.ACQUIRE_TOKEN_SUCCESS, EventType.HANDLE_REDIRECT_END].includes(eventType);
}
